import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  PayrollHoursRow,
  PayrollNotSplitByShop,
  PayrollPaidMonth,
  Location,
  PayrollAdjustmentKind,
  PayrollAdjustmentRecord,
  PayrollPaymentRecord,
  PayrollSummaryRow,
} from "../api/types";
import { Modal } from "../components/Modal";
import { OperatingCostsSection } from "../components/finances/OperatingCostsSection";
import { PurchasesIvaCard } from "../components/finances/PurchasesIvaCard";
import { SalesIvaCard } from "../components/finances/SalesIvaCard";
import { HaciendaBalanceCard } from "../components/finances/HaciendaBalanceCard";
import { TaxRatesCard } from "../components/finances/TaxRatesCard";
import { HoursCard } from "../components/finances/HoursCard";
import { ShopSalesTiles } from "../components/finances/ShopSalesTiles";
import { useAuth } from "../state/AuthContext";
import { CommissionRulesCard } from "../components/finances/CommissionRulesCard";
import { businessToday } from "../utils/businessDay";
import { parseTypedAmount } from "../utils/typedAmount";
import { AmountReadBack } from "../components/finances/AmountReadBack";
import { PayrollTotalNote, PayrollWageCell } from "../components/finances/PayrollWageCell";

const ADJUSTMENT_KIND_LABEL: Record<PayrollAdjustmentKind, string> = {
  BONUS: "Bonus",
  DEDUCTION: "Deduction",
};

const ADJUSTMENT_IMAGE_MAX_BYTES = 15 * 1024 * 1024;
const ADJUSTMENT_IMAGE_MIMES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

const PAYROLL_PAYMENT_TYPE_LABEL: Record<PayrollPaymentRecord["paymentType"], string> = {
  CASH: "Cash",
  BANK: "Bank",
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function toDatetimeLocalValue(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function monthBoundsUtc(year: number, month1to12: number): { first: Date; last: Date } {
  const first = new Date(Date.UTC(year, month1to12 - 1, 1));
  const lastDay = new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
  const last = new Date(Date.UTC(year, month1to12 - 1, lastDay));
  return { first, last };
}

function isoUtcYmd(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

function formatUtcIsoDayLabel(iso: string): string {
  const parts = iso.split("-").map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || !y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatEuro(raw: string) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return `€${n.toFixed(2)}`;
}

function payrollBalanceClass(balanceDueEur: string | null): string {
  if (balanceDueEur === null) return "";
  const bal = Number(balanceDueEur);
  if (bal < 0) return "finances-balance--credit";
  if (bal > 0) return "finances-balance--due";
  return "";
}

function PayrollRateCell({ row }: { row: PayrollSummaryRow }) {
  if (row.rateBreakdown.length <= 1) return <>{row.hourlyRateEur ?? "—"}</>;
  return (
    <div>
      <strong>Mixed</strong>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {row.rateBreakdown
          .map((b) => `${b.hours}h @ ${b.rateEur ? formatEuro(b.rateEur) : "—"}`)
          .join(" · ")}
      </div>
    </div>
  );
}

/**
 * G-163 (owner 2026-10-19): "Balance" is what would be handed over today —
 * this window plus the earlier months the server carries (credit lowers it).
 */
function balanceShown(row: PayrollSummaryRow): string | null {
  return row.balanceWithEarlierEur ?? row.balanceDueEur;
}

/** Paid and Balance are null under one shop (G-126, OD-152) — the table hides them there. */
function formatPaidSide(raw: string | null): string {
  return raw === null ? "—" : formatEuro(raw);
}

/**
 * G-126 (OD-152), under one shop: this person's salary, bonuses or deductions
 * are counted at their home shop for the month, not here (the note above the
 * table says so) — shown under the name, since it may be a bonus, not a salary.
 */
/** G-163 (D-78): a person who left stays listed in their months and while a balance remains. */
function InactivePill({ row }: { row: Pick<PayrollSummaryRow, "isActive"> }) {
  return row.isActive ? null : <span className="badge"> inactive</span>;
}

function HomeShopElsewhereHint({ row }: { row: PayrollSummaryRow }) {
  if (!row.salaryCountedAt) return null;
  return <span className="muted finances-counted-at">home shop: {row.salaryCountedAt.name}</span>;
}

/** KI-108 (G-163): the next pay day the server works out for the row. Owners are paid from profit (OD-151). */
function NextPayDayHint({ row }: { row: PayrollSummaryRow }) {
  if (row.role === "OWNER") return null;
  return <span className="muted finances-counted-at">Next pay day: {formatUtcIsoDayLabel(row.nextPayDay)}</span>;
}

/**
 * G-163 (KI-106): the window's salary, summed month by month from the salary
 * in force each month. A range can owe salary for months when the person was
 * salaried even if they are hourly now, so the figure shows whenever it is not
 * zero, not only when today's terms are salaried.
 */
function salaryText(row: PayrollSummaryRow): string {
  return row.monthlySalaryEur != null || Number(row.salaryOwedEur) !== 0 ? formatEuro(row.salaryOwedEur) : "—";
}

/**
 * G-168: labels, not sentences. The rule itself (what a one-shop view counts
 * where) is on docs/screens/crm/finances.md and finances.md §7.3.
 */
function PayrollShopViewNote({ notSplit }: { notSplit: PayrollNotSplitByShop | null }) {
  return (
    <p className="hint finances-shop-view-note">
      One shop · hours, wage, commission: worked and sold here · salary, bonuses, deductions: the
      person&apos;s home shop · paid and balance: All shops only
      {notSplit && notSplit.people > 0
        ? ` · ${notSplit.people === 1 ? "1 person" : `${notSplit.people} people`} with no home shop: salary ${formatEuro(
            notSplit.salaryEur,
          )}, bonuses ${formatEuro(notSplit.bonusEur)}, deductions ${formatEuro(
            notSplit.deductionEur,
          )} under All shops only`
        : null}
    </p>
  );
}

type PayrollSortColumn = "hours" | "totalOwed" | "paid" | "balance";

function payrollMobileSortSelectValue(
  column: PayrollSortColumn | null,
  direction: "asc" | "desc",
): string {
  if (column === null) return "__default__";
  return `${column}:${direction}`;
}

function parsePayrollMobileSortSelect(value: string): {
  column: PayrollSortColumn | null;
  direction: "asc" | "desc";
} {
  if (value === "__default__") return { column: null, direction: "desc" };
  const sep = value.lastIndexOf(":");
  const column = value.slice(0, sep) as PayrollSortColumn;
  const direction = value.slice(sep + 1) as "asc" | "desc";
  return { column, direction };
}

function sortPayrollRows(
  list: PayrollSummaryRow[],
  column: PayrollSortColumn | null,
  direction: "asc" | "desc",
): PayrollSummaryRow[] {
  if (!column) return list;
  const mult = direction === "asc" ? 1 : -1;
  const valueOf = (r: PayrollSummaryRow): number => {
    switch (column) {
      case "hours":
        return Number(r.hoursWorked) || 0;
      case "totalOwed":
        return Number(r.totalOwedEur) || 0;
      case "paid":
        return Number(r.totalPaidEur) || 0;
      case "balance":
        return Number(r.balanceWithEarlierEur ?? r.balanceDueEur) || 0;
      default:
        return 0;
    }
  };
  return [...list].sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va !== vb) return mult * (va < vb ? -1 : 1);
    return a.fullName.localeCompare(b.fullName);
  });
}

function PayrollSortHeader({
  label,
  column,
  activeColumn,
  direction,
  onSort,
}: {
  label: string;
  column: PayrollSortColumn;
  activeColumn: PayrollSortColumn | null;
  direction: "asc" | "desc";
  onSort: (col: PayrollSortColumn, dir: "asc" | "desc") => void;
}) {
  const active = activeColumn === column;
  const ariaSort = active
    ? direction === "asc"
      ? "ascending"
      : "descending"
    : undefined;

  return (
    <th scope="col" aria-sort={ariaSort}>
      <div className="finances-sort-th-inner">
        <span>{label}</span>
        <span className="finances-sort-arrows" role="group" aria-label={`Sort by ${label}`}>
          <button
            type="button"
            className={`finances-sort-arrow${active && direction === "asc" ? " is-active" : ""}`}
            aria-label={`${label}, ascending`}
            aria-pressed={active && direction === "asc"}
            onClick={() => onSort(column, "asc")}
          >
            ↑
          </button>
          <button
            type="button"
            className={`finances-sort-arrow${active && direction === "desc" ? " is-active" : ""}`}
            aria-label={`${label}, descending`}
            aria-pressed={active && direction === "desc"}
            onClick={() => onSort(column, "desc")}
          >
            ↓
          </button>
        </span>
      </div>
    </th>
  );
}

export function FinancesPage() {
  const { user, hasRole } = useAuth();
  const ownerScoped = hasRole("OWNER");
  const showPayments = hasRole("OWNER");
  /** Owner: any non-owner row. Manager: any non-owner row in scope (backend already scopes the roster). */
  const canIssueDeductions = hasRole("OWNER") || hasRole("MANAGER");
  /** Bonuses are owner-only per business rules. */
  const canIssueBonuses = hasRole("OWNER");
  const showActionsColumn = showPayments || canIssueDeductions || canIssueBonuses;

  // G-126 (OD-153, owner 2026-10-02): the page opens on THIS month, on the
  // Barcelona clock the server counts in ([145]: the old default was a UTC
  // "1st of the month before last → today" range that charged three salaries).
  // D-33: it is also the bookkeeping month a payment/adjustment defaults to.
  const today = useMemo(() => businessToday(), []);
  // G-163: "?month=YYYY-MM" opens another month — the home page's "Pay day
  // passed" row opens the month that is owed, so a payment is booked to it.
  const [searchParams] = useSearchParams();
  const openedMonth = useMemo(() => {
    const m = /^(\d{4})-(\d{2})$/.exec(searchParams.get("month") ?? "");
    const month = m ? Number(m[2]) : 0;
    return m && month >= 1 && month <= 12 ? { year: Number(m[1]), month } : null;
  }, [searchParams]);
  const [periodYear, setPeriodYear] = useState(openedMonth?.year ?? today.year);
  const [periodMonth, setPeriodMonth] = useState(openedMonth?.month ?? today.month);

  type AppliedPayrollRange = { from: string; to: string };
  const [appliedRange, setAppliedRange] = useState<AppliedPayrollRange | null>(null);
  const [draftRangeFrom, setDraftRangeFrom] = useState("");
  const [draftRangeTo, setDraftRangeTo] = useState("");
  const [rangeDraftError, setRangeDraftError] = useState<string | null>(null);
  const [mobileDateRangeOpen, setMobileDateRangeOpen] = useState(false);
  const dateRangeFieldsId = useId();
  const payrollMobileSortId = useId();

  type PayrollSummaryMeta = {
    /** null in range mode (D-33) — the modals then default to the current month. */
    periodYear: number | null;
    periodMonth: number | null;
    viewMode: "month" | "range";
    rangeFrom: string | null;
    rangeTo: string | null;
    /** G-126 (OD-152): one shop shows cost only — paid and balance are All-shops figures. */
    shopView: boolean;
    notSplitByShop: PayrollNotSplitByShop | null;
  };
  const [summaryMeta, setSummaryMeta] = useState<PayrollSummaryMeta | null>(null);

  const [locations, setLocations] = useState<Location[]>([]);
  const [locationId, setLocationId] = useState<string>("");

  const [rows, setRows] = useState<PayrollSummaryRow[]>([]);
  /**
   * G-108 (OD-79): the SERVER decides whether money is shown — a manager's
   * rows arrive as hours alone. Seeded from the role only so the first paint
   * does not flash the owner's money headers at a manager.
   */
  const [payVisible, setPayVisible] = useState(ownerScoped);
  const [hoursRows, setHoursRows] = useState<PayrollHoursRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [recordTarget, setRecordTarget] = useState<PayrollSummaryRow | null>(null);
  const [adjustmentTarget, setAdjustmentTarget] = useState<{
    row: PayrollSummaryRow | PayrollHoursRow;
    kind: PayrollAdjustmentKind;
  } | null>(null);

  const [sortColumn, setSortColumn] = useState<PayrollSortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const sortedRows = useMemo(
    () => sortPayrollRows(rows, sortColumn, sortDirection),
    [rows, sortColumn, sortDirection],
  );

  const shopView = summaryMeta?.shopView ?? false;
  /** G-163: "Earlier months" once the server states it (All shops, carry-forward set). */
  const showEarlier = !shopView && rows.some((r) => r.unpaidEarlierEur !== null);
  /** Name … Deduct (9) + Total, then Paid, Earlier months and Balance under All shops, then Actions. */
  const tableColumns = 10 + (shopView ? 0 : 2) + (showEarlier ? 1 : 0) + (showActionsColumn ? 1 : 0);

  const handlePayrollSort = (col: PayrollSortColumn, dir: "asc" | "desc") => {
    setSortColumn(col);
    setSortDirection(dir);
  };

  useEffect(() => {
    if (!ownerScoped) return;
    void (async () => {
      try {
        const list = await api.listLocations();
        setLocations(list.filter((l) => l.isActive));
      } catch {
        setLocations([]);
      }
    })();
  }, [ownerScoped]);

  useEffect(() => {
    if (appliedRange) {
      setDraftRangeFrom(appliedRange.from);
      setDraftRangeTo(appliedRange.to);
      return;
    }
    const { first, last } = monthBoundsUtc(periodYear, periodMonth);
    setDraftRangeFrom(isoUtcYmd(first));
    setDraftRangeTo(isoUtcYmd(last));
  }, [periodYear, periodMonth, appliedRange]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const loc = ownerScoped && locationId ? locationId : undefined;
      const data = appliedRange
        ? await api.getPayrollSummary({
            from: appliedRange.from,
            to: appliedRange.to,
            locationId: loc,
          })
        : await api.getPayrollSummary({
            year: periodYear,
            month: periodMonth,
            locationId: loc,
          });
      if (data.payVisible) {
        setRows(data.rows);
        setHoursRows([]);
      } else {
        setRows([]);
        setHoursRows(data.rows);
      }
      setPayVisible(data.payVisible);
      setSummaryMeta({
        periodYear: data.periodYear,
        periodMonth: data.periodMonth,
        viewMode: data.viewMode,
        rangeFrom: data.rangeFrom,
        rangeTo: data.rangeTo,
        shopView: data.locationScope !== "all_shops",
        notSplitByShop: data.payVisible ? data.notSplitByShop : null,
      });
    } catch (err: unknown) {
      setRows([]);
      setHoursRows([]);
      setSummaryMeta(null);
      setError(err instanceof Error ? err.message : "Could not load payroll");
    } finally {
      setLoading(false);
    }
  }, [periodYear, periodMonth, locationId, ownerScoped, appliedRange]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const periodLabel = useMemo(() => {
    if (appliedRange) {
      return `${formatUtcIsoDayLabel(appliedRange.from)} – ${formatUtcIsoDayLabel(appliedRange.to)}`;
    }
    const { first } = monthBoundsUtc(periodYear, periodMonth);
    return first.toLocaleString(undefined, {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
  }, [appliedRange, periodYear, periodMonth]);

  const applyCustomRange = () => {
    setRangeDraftError(null);
    if (!draftRangeFrom || !draftRangeTo) {
      setRangeDraftError("Choose both start and end dates.");
      return;
    }
    if (draftRangeFrom > draftRangeTo) {
      setRangeDraftError("Start date must be on or before end date.");
      return;
    }
    setAppliedRange({ from: draftRangeFrom, to: draftRangeTo });
    setMobileDateRangeOpen(false);
  };

  const clearCustomRange = () => {
    setAppliedRange(null);
    setRangeDraftError(null);
  };

  const shiftMonth = (delta: number) => {
    setAppliedRange(null);
    const m0 = periodMonth - 1 + delta;
    const d = new Date(Date.UTC(periodYear, m0, 1));
    setPeriodYear(d.getUTCFullYear());
    setPeriodMonth(d.getUTCMonth() + 1);
  };

  const managerShopLabel =
    !ownerScoped && user?.assignedLocation
      ? `${user.assignedLocation.name} (${user.assignedLocation.code})`
      : null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Finances</h2>
          <p className="page-subtitle">
            {payVisible
              ? "Payroll · running costs · purchases & IVA paid · pricing rules · Barcelona days"
              : (managerShopLabel ?? "No shop assignment — payroll unavailable.")}
          </p>
        </div>
      </div>

      {payVisible ? null : <ShopSalesTiles />}

      {payVisible ? (
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>Salary</h3>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "var(--space-3)",
                alignItems: "flex-end",
              }}
            >
              <div className="schedule-month-nav" style={{ flex: "1 1 auto" }}>
                <button type="button" className="button tinted" onClick={() => shiftMonth(-1)}>
                  ‹ Prev
                </button>
                <span className="schedule-month-label">{periodLabel}</span>
                <button type="button" className="button tinted" onClick={() => shiftMonth(1)}>
                  Next ›
                </button>
                <button
                  type="button"
                  className="button plain"
                  onClick={() => {
                    setAppliedRange(null);
                    const t = businessToday();
                    setPeriodYear(t.year);
                    setPeriodMonth(t.month);
                  }}
                >
                  This month
                </button>
              </div>
              {ownerScoped ? (
                <div className="field" style={{ minWidth: "14rem", flex: "1 1 12rem" }}>
                  <label htmlFor="payroll-location">Shop</label>
                  <select
                    id="payroll-location"
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      // Paid and Balance leave the table under one shop (OD-152):
                      // a sort on them would order rows by nothing.
                      if (e.target.value && (sortColumn === "paid" || sortColumn === "balance")) {
                        setSortColumn(null);
                      }
                    }}
                  >
                    <option value="">All locations</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
            </div>
            <div className="finances-custom-range">
              <span className="muted finances-custom-range-label">Custom range (Barcelona days)</span>
              <button
                type="button"
                className="button tinted finances-custom-range-phone-toggle"
                aria-expanded={mobileDateRangeOpen}
                aria-controls={dateRangeFieldsId}
                onClick={() => setMobileDateRangeOpen((v) => !v)}
              >
                Date Range
              </button>
              <div
                id={dateRangeFieldsId}
                className={
                  mobileDateRangeOpen
                    ? "finances-custom-range-fields finances-custom-range-fields--open"
                    : "finances-custom-range-fields"
                }
              >
                <div className="field finances-custom-range-field">
                  <label htmlFor="payroll-range-from">From</label>
                  <input
                    id="payroll-range-from"
                    type="date"
                    value={draftRangeFrom}
                    onChange={(e) => setDraftRangeFrom(e.target.value)}
                  />
                </div>
                <div className="field finances-custom-range-field">
                  <label htmlFor="payroll-range-to">To</label>
                  <input
                    id="payroll-range-to"
                    type="date"
                    value={draftRangeTo}
                    onChange={(e) => setDraftRangeTo(e.target.value)}
                  />
                </div>
                <button type="button" className="button tinted" onClick={applyCustomRange}>
                  Apply range
                </button>
                <button
                  type="button"
                  className="button plain"
                  onClick={clearCustomRange}
                  disabled={!appliedRange}
                >
                  Clear range
                </button>
              </div>
              {rangeDraftError ? (
                <p className="hint finances-custom-range-error" style={{ margin: 0 }}>
                  {rangeDraftError}
                </p>
              ) : null}
            </div>
          </div>
      ) : (
        <HoursCard
          year={periodYear}
          month={periodMonth}
          onMonthChange={(y, m) => {
            setPeriodYear(y);
            setPeriodMonth(m);
          }}
          rows={hoursRows}
          loading={loading}
          canDeduct={canIssueDeductions}
          onDeduct={(row) => setAdjustmentTarget({ row, kind: "DEDUCTION" })}
        />
      )}

      {error ? <div className="alert error">{error}</div> : null}

      {payVisible ? (
        <div className="card finances-pay-table-card">
          {shopView ? <PayrollShopViewNote notSplit={summaryMeta?.notSplitByShop ?? null} /> : null}
          <div className="finances-pay-desktop-only">
            <div
              className="table-scroll finances-pay-table-scroll"
              tabIndex={0}
              role="region"
              aria-label="Salary breakdown"
            >
              <table className="table finances-pay-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <PayrollSortHeader
                      label="Hours"
                      column="hours"
                      activeColumn={sortColumn}
                      direction={sortDirection}
                      onSort={handlePayrollSort}
                    />
                    <th>Rate €/h</th>
                    <th>Wage</th>
                    <th>Salary</th>
                    <th>Comm. %</th>
                    <th>Commission</th>
                    <th>Bonus</th>
                    <th>Deduct</th>
                    <PayrollSortHeader
                      label={shopView ? "Cost at this shop" : "Total owed"}
                      column="totalOwed"
                      activeColumn={sortColumn}
                      direction={sortDirection}
                      onSort={handlePayrollSort}
                    />
                    {shopView ? null : (
                      <>
                        <PayrollSortHeader
                          label="Paid"
                          column="paid"
                          activeColumn={sortColumn}
                          direction={sortDirection}
                          onSort={handlePayrollSort}
                        />
                        {showEarlier ? <th>Earlier months</th> : null}
                        <PayrollSortHeader
                          label="Balance"
                          column="balance"
                          activeColumn={sortColumn}
                          direction={sortDirection}
                          onSort={handlePayrollSort}
                        />
                      </>
                    )}
                    {showActionsColumn ? (
                      <th className="finances-pay-actions-col">Actions</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={tableColumns} style={{ padding: "var(--space-4)" }}>
                        Loading…
                      </td>
                    </tr>
                  ) : sortedRows.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumns} style={{ padding: "var(--space-4)" }}>
                        No staff in scope for this period.
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((r) => {
                      const balClass = payrollBalanceClass(balanceShown(r));
                      // OD-151: an owner's row shows hours only — the server
                      // refuses payments and adjustments against owners.
                      const rowIsOwner = r.role === "OWNER";
                      return (
                        <tr key={r.userId}>
                          <td>
                            <strong>{r.fullName}</strong> <InactivePill row={r} />
                            <HomeShopElsewhereHint row={r} />
                            <NextPayDayHint row={r} />
                          </td>
                          <td>
                            {r.hoursWorked}
                            {r.inProgressShiftCount > 0 ? (
                              <>
                                {" "}
                                <span
                                  className="badge warning"
                                  title="A shift still in progress pays nothing until it is ended (D-32)"
                                >
                                  {r.inProgressShiftCount} not ended
                                </span>
                              </>
                            ) : null}
                          </td>
                          <td>
                            <PayrollRateCell row={r} />
                          </td>
                          <td>
                            <PayrollWageCell row={r} format={formatEuro} />
                          </td>
                          <td>{salaryText(r)}</td>
                          <td>{r.salesCommissionPercent ?? "—"}</td>
                          <td>{formatEuro(r.commissionOwedEur)}</td>
                          <td className="finances-bonus-cell">{formatEuro(r.bonusEur)}</td>
                          <td className="finances-deduct-cell">
                            {Number(r.deductionEur) > 0 ? `− ${formatEuro(r.deductionEur)}` : formatEuro(r.deductionEur)}
                          </td>
                          <td>
                            {formatEuro(r.totalOwedEur)}
                            <PayrollTotalNote row={r} />
                          </td>
                          {shopView ? null : (
                            <>
                              <td>{formatPaidSide(r.totalPaidEur)}</td>
                              {showEarlier ? <td>{formatPaidSide(r.unpaidEarlierEur)}</td> : null}
                              <td className={balClass}>{formatPaidSide(balanceShown(r))}</td>
                            </>
                          )}
                          {showActionsColumn ? (
                            <td className="finances-pay-actions-col">
                              <div className="finances-actions-stack">
                                {showPayments && !rowIsOwner ? (
                                  <button
                                    type="button"
                                    className="button tinted"
                                    onClick={() => setRecordTarget(r)}
                                  >
                                    Record payment
                                  </button>
                                ) : null}
                                {canIssueBonuses && !rowIsOwner ? (
                                  <button
                                    type="button"
                                    className="button plain"
                                    onClick={() => setAdjustmentTarget({ row: r, kind: "BONUS" })}
                                  >
                                    Bonus
                                  </button>
                                ) : null}
                                {canIssueDeductions && !rowIsOwner && (r.isActive || canIssueBonuses) ? (
                                  <button
                                    type="button"
                                    className="button plain"
                                    onClick={() => setAdjustmentTarget({ row: r, kind: "DEDUCTION" })}
                                  >
                                    Deduct from Salary
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="finances-pay-mobile-only" aria-label="Salary breakdown">
            {loading ? (
              <p className="muted" style={{ margin: 0 }}>
                Loading…
              </p>
            ) : sortedRows.length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>
                No staff in scope for this period.
              </p>
            ) : (
              <>
                <div className="field finances-pay-mobile-sort">
                  <label htmlFor={payrollMobileSortId}>Sort by</label>
                  <select
                    id={payrollMobileSortId}
                    value={payrollMobileSortSelectValue(sortColumn, sortDirection)}
                    onChange={(e) => {
                      const parsed = parsePayrollMobileSortSelect(e.target.value);
                      setSortColumn(parsed.column);
                      setSortDirection(parsed.direction);
                    }}
                  >
                    <option value="__default__">Default order</option>
                    {shopView ? null : (
                      <>
                        <option value="balance:desc">Balance · highest first</option>
                        <option value="balance:asc">Balance · lowest first</option>
                      </>
                    )}
                    <option value="totalOwed:desc">
                      {shopView ? "Cost at this shop" : "Total owed"} · highest first
                    </option>
                    <option value="totalOwed:asc">
                      {shopView ? "Cost at this shop" : "Total owed"} · lowest first
                    </option>
                    {shopView ? null : (
                      <>
                        <option value="paid:desc">Paid · highest first</option>
                        <option value="paid:asc">Paid · lowest first</option>
                      </>
                    )}
                    <option value="hours:desc">Hours · highest first</option>
                    <option value="hours:asc">Hours · lowest first</option>
                  </select>
                </div>
                <div className="finances-pay-cards">
                  {sortedRows.map((r) => {
                    const balClass = payrollBalanceClass(balanceShown(r));
                    const rowIsOwner = r.role === "OWNER";
                    return (
                      <article key={r.userId} className="finances-pay-card">
                        <h4 className="finances-pay-card-name">
                          {r.fullName} <InactivePill row={r} />
                        </h4>
                        <HomeShopElsewhereHint row={r} />
                        <NextPayDayHint row={r} />
                        <dl className="finances-pay-card-metrics">
                          <div className="finances-pay-card-row">
                            <dt>Hours</dt>
                            <dd>
                              {r.hoursWorked}
                              {r.inProgressShiftCount > 0 ? (
                                <>
                                  {" "}
                                  <span className="badge warning">{r.inProgressShiftCount} not ended</span>
                                </>
                              ) : null}
                            </dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Rate €/h</dt>
                            <dd>
                              <PayrollRateCell row={r} />
                            </dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Wage</dt>
                            <dd>
                              <PayrollWageCell row={r} format={formatEuro} />
                            </dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Salary</dt>
                            <dd>{salaryText(r)}</dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Comm. %</dt>
                            <dd>{r.salesCommissionPercent ?? "—"}</dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Commission</dt>
                            <dd>{formatEuro(r.commissionOwedEur)}</dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Bonus</dt>
                            <dd>{formatEuro(r.bonusEur)}</dd>
                          </div>
                          <div className="finances-pay-card-row">
                            <dt>Deduct</dt>
                            <dd>
                              {Number(r.deductionEur) > 0
                                ? `− ${formatEuro(r.deductionEur)}`
                                : formatEuro(r.deductionEur)}
                            </dd>
                          </div>
                          <div className="finances-pay-card-row finances-pay-card-row--emphasis">
                            <dt>{shopView ? "Cost at this shop" : "Total owed"}</dt>
                            <dd>
                              {formatEuro(r.totalOwedEur)}
                              <PayrollTotalNote row={r} />
                            </dd>
                          </div>
                          {shopView ? null : (
                            <>
                              <div className="finances-pay-card-row">
                                <dt>Paid</dt>
                                <dd>{formatPaidSide(r.totalPaidEur)}</dd>
                              </div>
                              {showEarlier ? (
                                <div className="finances-pay-card-row">
                                  <dt>Earlier months</dt>
                                  <dd>{formatPaidSide(r.unpaidEarlierEur)}</dd>
                                </div>
                              ) : null}
                              <div className="finances-pay-card-row finances-pay-card-row--balance">
                                <dt>Balance</dt>
                                <dd className={balClass}>{formatPaidSide(balanceShown(r))}</dd>
                              </div>
                            </>
                          )}
                        </dl>
                        <div className="finances-actions-stack finances-pay-card-action">
                          {showPayments && !rowIsOwner ? (
                            <button
                              type="button"
                              className="button tinted"
                              onClick={() => setRecordTarget(r)}
                            >
                              Record payment
                            </button>
                          ) : null}
                          {canIssueBonuses && !rowIsOwner ? (
                            <button
                              type="button"
                              className="button plain"
                              onClick={() => setAdjustmentTarget({ row: r, kind: "BONUS" })}
                            >
                              Bonus
                            </button>
                          ) : null}
                          {canIssueDeductions && !rowIsOwner && (r.isActive || canIssueBonuses) ? (
                            <button
                              type="button"
                              className="button plain"
                              onClick={() => setAdjustmentTarget({ row: r, kind: "DEDUCTION" })}
                            >
                              Deduct from Salary
                            </button>
                          ) : null}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

      ) : null}

      {/* G-32 running costs + break-even and G-39 one-off expenses. Own data
          fetching, own period control — deliberately independent of the payroll
          window above, which is a rolling range rather than a calendar month. */}
      <OperatingCostsSection />

      {/* G-121 S4: owner only — the card renders nothing for anyone else. */}
      <PurchasesIvaCard />

      {/* G-171: IVA on sales — owner, or a manager for their own shop (the
          server scopes); the balance with Hacienda — owner only. Each card
          renders nothing for anyone else. */}
      <SalesIvaCard />
      <HaciendaBalanceCard />

      {/* G-30: owner-only VAT rates per category. Lives on Finances by owner
          decision — this page is the business-settings home. */}
      <TaxRatesCard />

      {recordTarget ? (
        <RecordPayrollPaymentModal
          row={recordTarget}
          periodYear={summaryMeta?.periodYear ?? periodYear}
          periodMonth={summaryMeta?.periodMonth ?? periodMonth}
          viewMode={summaryMeta?.viewMode ?? "month"}
          summaryRangeFrom={summaryMeta?.rangeFrom ?? null}
          summaryRangeTo={summaryMeta?.rangeTo ?? null}
          onClose={() => setRecordTarget(null)}
          onSaved={async () => {
            setRecordTarget(null);
            await refresh();
          }}
        />
      ) : null}

      {adjustmentTarget ? (
        <RecordPayrollAdjustmentModal
          row={adjustmentTarget.row}
          kind={adjustmentTarget.kind}
          periodYear={summaryMeta?.periodYear ?? periodYear}
          periodMonth={summaryMeta?.periodMonth ?? periodMonth}
          onClose={() => setAdjustmentTarget(null)}
          onSaved={async () => {
            setAdjustmentTarget(null);
            await refresh();
          }}
        />
      ) : null}

      {/* G-22: owner-only — the endpoint refuses managers regardless. */}
      {ownerScoped ? <CommissionRulesCard /> : null}
    </section>
  );
}

/**
 * D-33: the bookkeeping month a payment or adjustment belongs to, chosen in
 * the modal rather than inherited from the summary's range. ONE field for
 * both modals.
 */
function BookkeepingMonthField({
  id,
  label,
  year,
  month,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  year: number;
  month: number;
  onChange: (year: number, month: number) => void;
  hint?: string;
}) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="month"
        value={`${year}-${pad2(month)}`}
        onChange={(e) => {
          const [y, m] = e.target.value.split("-").map(Number);
          if (y && m) onChange(y, m);
        }}
      />
      {hint ? <small className="hint">{hint}</small> : null}
    </div>
  );
}

function RecordPayrollPaymentModal({
  row,
  periodYear,
  periodMonth,
  viewMode,
  summaryRangeFrom,
  summaryRangeTo,
  onClose,
  onSaved,
}: {
  row: PayrollSummaryRow;
  periodYear: number;
  periodMonth: number;
  viewMode: "month" | "range";
  summaryRangeFrom: string | null;
  summaryRangeTo: string | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [amountStr, setAmountStr] = useState("");
  const [paymentType, setPaymentType] = useState<PayrollPaymentRecord["paymentType"]>("BANK");
  const [paidAtLocal, setPaidAtLocal] = useState(() => toDatetimeLocalValue(new Date()));
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [lines, setLines] = useState<PayrollPaymentRecord[] | null>(null);
  // G-126 ([140]): a bonus counts as paid when it is recorded (OD-83), so the
  // table's "Paid" includes it — the window lists it too, or the two disagree.
  const [bonuses, setBonuses] = useState<PayrollAdjustmentRecord[] | null>(null);
  // The month's paid figure comes from the server's one rule (payments +
  // bonuses), never added up here — the table's Paid and this line must match.
  const [paidMonth, setPaidMonth] = useState<PayrollPaidMonth | null>(null);
  // D-33: the bookkeeping month is chosen HERE, defaulting to the summary's
  // month (or the current month in range mode) — never silently the range's
  // first month.
  const [bookYear, setBookYear] = useState(periodYear);
  const [bookMonth, setBookMonth] = useState(periodMonth);

  // Three independent loads: one failing must not blank the others — an empty
  // "Payments this month" over a real payment invites recording it twice.
  const loadMonth = useCallback(async () => {
    const q = { userId: row.userId, year: bookYear, month: bookMonth };
    await Promise.all([
      api.listPayrollPayments(q).then(setLines, () => setLines([])),
      api
        .listPayrollAdjustments(q)
        .then((all) => setBonuses(all.rows.filter((a) => a.kind === "BONUS")), () => setBonuses([])),
      api.getPayrollPaidMonth(q).then(setPaidMonth, () => setPaidMonth(null)),
    ]);
  }, [row.userId, bookYear, bookMonth]);

  useEffect(() => {
    void loadMonth();
  }, [loadMonth]);

  const submit = async () => {
    setLocalError(null);
    const read = parseTypedAmount(amountStr);
    if (!read.ok) {
      setLocalError(read.message);
      return;
    }
    const amount = read.value;
    if (amount <= 0) {
      setLocalError("Enter a positive amount.");
      return;
    }
    const paidAt = new Date(paidAtLocal);
    if (Number.isNaN(paidAt.getTime())) {
      setLocalError("Invalid date/time.");
      return;
    }
    setSubmitting(true);
    try {
      await api.createPayrollPayment({
        userId: row.userId,
        periodYear: bookYear,
        periodMonth: bookMonth,
        amountEur: amount,
        paymentType,
        paidAt: paidAt.toISOString(),
        notes: notes.trim() || undefined,
      });
      await onSaved();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : "Could not save payment");
    } finally {
      setSubmitting(false);
    }
  };

  const deletePayment = async (id: string) => {
    if (!confirm("Delete this payment record?")) return;
    setDeletingId(id);
    setLocalError(null);
    try {
      await api.deletePayrollPayment(id);
      await loadMonth();
      await onSaved();
    } catch (err: unknown) {
      setLocalError(err instanceof Error ? err.message : "Could not delete payment");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal title={`Record payment · ${row.fullName}`} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          {viewMode === "range" && summaryRangeFrom && summaryRangeTo
            ? `Summary range ${summaryRangeFrom}–${summaryRangeTo} · `
            : ""}
          {row.balanceDueEur === null ? (
            "The balance is a person's whole month — choose All shops to see it."
          ) : (
            <>
              Outstanding balance before this payment: <strong>{formatEuro(row.balanceDueEur)}</strong>
            </>
          )}
        </p>
        {localError ? <div className="alert error">{localError}</div> : null}
        <BookkeepingMonthField
          id="payroll-pay-period"
          label="Payroll month this payment belongs to"
          year={bookYear}
          month={bookMonth}
          onChange={(y, m) => {
            setBookYear(y);
            setBookMonth(m);
          }}
          hint="An advance paid mid-month belongs to that month; the balance above is for the summary you are looking at."
        />
        <div className="field">
          <label htmlFor="payroll-pay-amount">Amount (EUR)</label>
          <input
            id="payroll-pay-amount"
            type="text"
            inputMode="decimal"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="e.g. 350"
          />
          <AmountReadBack raw={amountStr} />
        </div>
        <div className="field">
          <label htmlFor="payroll-pay-when">Paid at</label>
          <input
            id="payroll-pay-when"
            type="datetime-local"
            value={paidAtLocal}
            onChange={(e) => setPaidAtLocal(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="payroll-pay-payment-type">Payment type</label>
          <select
            id="payroll-pay-payment-type"
            value={paymentType}
            onChange={(e) =>
              setPaymentType(e.target.value as PayrollPaymentRecord["paymentType"])
            }
          >
            <option value="CASH">{PAYROLL_PAYMENT_TYPE_LABEL.CASH}</option>
            <option value="BANK">{PAYROLL_PAYMENT_TYPE_LABEL.BANK}</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="payroll-pay-notes">Notes (optional)</label>
          <input
            id="payroll-pay-notes"
            type="text"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button" onClick={() => void submit()} disabled={submitting}>
            {submitting ? "Saving…" : "Save payment"}
          </button>
        </div>
        <div>
          <strong className="finances-pay-list-heading">Payments this month</strong>
          {!lines ? (
            <p className="muted">Loading…</p>
          ) : lines.length === 0 ? (
            <p className="muted">None recorded yet.</p>
          ) : (
            <ul className="finances-pay-list">
              {lines.map((p) => (
                <li key={p.id}>
                  <span>{formatEuro(p.amountEur)}</span>{" "}
                  <span className="muted">
                    · {PAYROLL_PAYMENT_TYPE_LABEL[p.paymentType]} ·{" "}
                    {new Date(p.paidAt).toLocaleString()} · by {p.recordedBy.fullName}
                  </span>
                  {p.notes ? <div className="muted finances-pay-list-note">{p.notes}</div> : null}
                  <button
                    type="button"
                    className="button plain"
                    style={{ marginTop: 6 }}
                    onClick={() => void deletePayment(p.id)}
                    disabled={deletingId === p.id}
                  >
                    {deletingId === p.id ? "Deleting…" : "Delete"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {bonuses && bonuses.length > 0 ? (
          <div>
            <strong className="finances-pay-list-heading">Bonuses this month — handed over when recorded</strong>
            <ul className="finances-pay-list">
              {bonuses.map((b) => (
                <li key={b.id}>
                  <span>{formatEuro(b.amountEur)}</span>{" "}
                  <span className="muted">
                    · {b.reason} · by {b.recordedBy.fullName}
                  </span>
                </li>
              ))}
            </ul>
            {paidMonth ? (
              <p className="finances-paid-sum">
                Paid this month: payments {formatEuro(paidMonth.paymentsEur)} + bonuses{" "}
                {formatEuro(paidMonth.bonusesEur)} = <strong>{formatEuro(paidMonth.paidEur)}</strong>
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}


function AdjustmentImageThumb({ id }: { id: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      const url = await api.fetchPayrollAdjustmentImageBlobUrl(id);
      if (cancelled) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      setSrc(url);
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  if (!src) return <span className="muted" style={{ fontSize: 12 }}>(loading image…)</span>;
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img
        src={src}
        alt=""
        style={{
          maxWidth: 72,
          maxHeight: 72,
          borderRadius: 4,
          border: "1px solid var(--color-border-soft)",
          objectFit: "cover",
        }}
      />
    </a>
  );
}

function RecordPayrollAdjustmentModal({
  row,
  kind,
  periodYear,
  periodMonth,
  onClose,
  onSaved,
}: {
  row: PayrollSummaryRow | PayrollHoursRow;
  kind: PayrollAdjustmentKind;
  periodYear: number;
  periodMonth: number;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { user, hasRole } = useAuth();
  const [amountStr, setAmountStr] = useState("");
  const [reason, setReason] = useState("");
  const [occurredAtLocal, setOccurredAtLocal] = useState(() =>
    toDatetimeLocalValue(new Date()),
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [lines, setLines] = useState<PayrollAdjustmentRecord[] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // D-33: see RecordPayrollPaymentModal — the same chosen bookkeeping month.
  const [bookYear, setBookYear] = useState(periodYear);
  const [bookMonth, setBookMonth] = useState(periodMonth);

  const refreshLines = useCallback(async () => {
    try {
      const list = await api.listPayrollAdjustments({
        userId: row.userId,
        year: bookYear,
        month: bookMonth,
      });
      setLines(list.rows);
    } catch {
      setLines([]);
    }
  }, [row.userId, bookYear, bookMonth]);

  useEffect(() => {
    void refreshLines();
  }, [refreshLines]);

  const handlePickImage = (file: File | null) => {
    setImageError(null);
    if (!file) {
      setImageFile(null);
      return;
    }
    if (file.size > ADJUSTMENT_IMAGE_MAX_BYTES) {
      setImageError("Image exceeds 15 MB limit.");
      return;
    }
    if (!ADJUSTMENT_IMAGE_MIMES.includes(file.type)) {
      setImageError("Image must be JPEG, PNG, or WEBP.");
      return;
    }
    setImageFile(file);
  };

  const submit = async () => {
    setLocalError(null);
    const read = parseTypedAmount(amountStr);
    if (!read.ok) {
      setLocalError(read.message);
      return;
    }
    const amount = read.value;
    if (amount <= 0) {
      setLocalError("Enter a positive amount.");
      return;
    }
    if (reason.trim().length < 3) {
      setLocalError("Reason must be at least 3 characters.");
      return;
    }
    const occurredAt = new Date(occurredAtLocal);
    if (Number.isNaN(occurredAt.getTime())) {
      setLocalError("Invalid date/time.");
      return;
    }
    setSubmitting(true);
    try {
      await api.createPayrollAdjustment({
        kind,
        userId: row.userId,
        amountEur: amount,
        reason: reason.trim(),
        periodYear: bookYear,
        periodMonth: bookMonth,
        occurredAt: occurredAt.toISOString(),
        image: imageFile ?? undefined,
      });
      await onSaved();
    } catch (err: unknown) {
      setLocalError(
        err instanceof Error ? err.message : "Could not save adjustment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canDelete = (a: PayrollAdjustmentRecord): boolean => {
    if (hasRole("OWNER")) return true;
    if (a.recordedBy.id !== user?.id) return false;
    const ageMs = Date.now() - new Date(a.createdAt).getTime();
    return ageMs <= 24 * 60 * 60 * 1000;
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this adjustment? This cannot be undone.")) return;
    setDeletingId(id);
    try {
      await api.deletePayrollAdjustment(id);
      await refreshLines();
      await onSaved();
    } catch (err: unknown) {
      setLocalError(
        err instanceof Error ? err.message : "Could not delete adjustment",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const title =
    kind === "BONUS"
      ? `Bonus · ${row.fullName}`
      : `Deduct from salary · ${row.fullName}`;
  const submitLabel =
    kind === "BONUS" ? "Save bonus" : "Save deduction";

  return (
    <Modal title={title} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p className="muted" style={{ margin: 0, fontSize: 14 }}>
          Payroll month {bookYear}-{pad2(bookMonth)}
          {/* G-108: only an owner's row carries a balance; G-126: none under one shop. */}
          {"balanceDueEur" in row && row.balanceDueEur !== null ? (
            <>
              {" "}· Current balance: <strong>{formatEuro(row.balanceDueEur)}</strong>
            </>
          ) : null}
          {kind === "BONUS"
            ? " · Recording a bonus also counts it as paid (the owner hands it over at issuance). " +
              "Don't include the bonus amount when you later record a salary payment."
            : " · A deduction reduces what's owed to this employee."}
        </p>
        {localError ? <div className="alert error">{localError}</div> : null}

        <BookkeepingMonthField
          id="payroll-adj-period"
          label="Payroll month this belongs to"
          year={bookYear}
          month={bookMonth}
          onChange={(y, m) => {
            setBookYear(y);
            setBookMonth(m);
          }}
        />
        <div className="field">
          <label htmlFor="payroll-adj-amount">Amount (EUR)</label>
          <input
            id="payroll-adj-amount"
            type="text"
            inputMode="decimal"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            placeholder="e.g. 50"
          />
          <AmountReadBack raw={amountStr} />
        </div>
        <div className="field">
          <label htmlFor="payroll-adj-reason">Reason</label>
          <textarea
            id="payroll-adj-reason"
            maxLength={500}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={
              kind === "BONUS"
                ? "e.g. Holiday bonus, exceptional performance"
                : "e.g. Damaged stock, cash drawer shortage"
            }
          />
        </div>
        <div className="field">
          <label htmlFor="payroll-adj-when">When did this occur?</label>
          <input
            id="payroll-adj-when"
            type="datetime-local"
            value={occurredAtLocal}
            onChange={(e) => setOccurredAtLocal(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="payroll-adj-image">Photo (optional · JPEG/PNG/WEBP/HEIC, ≤15 MB)</label>
          <input
            id="payroll-adj-image"
            type="file"
            accept={ADJUSTMENT_IMAGE_MIMES.join(",")}
            onChange={(e) => handlePickImage(e.target.files?.[0] ?? null)}
          />
          {imageError ? <p className="hint" style={{ margin: 0 }}>{imageError}</p> : null}
          {imageFile ? (
            <p className="muted" style={{ margin: "var(--space-1) 0 0", fontSize: 13 }}>
              Selected: {imageFile.name} ({(imageFile.size / 1024).toFixed(0)} KB)
            </p>
          ) : null}
        </div>

        <div className="button-row">
          <button type="button" className="button plain" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void submit()}
            disabled={submitting}
          >
            {submitting ? "Saving…" : submitLabel}
          </button>
        </div>

        <div>
          <strong style={{ fontSize: 13 }}>Adjustments this month</strong>
          {!lines ? (
            <p className="muted">Loading…</p>
          ) : lines.length === 0 ? (
            <p className="muted">None recorded yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: "var(--space-2) 0 0", fontSize: 14 }}>
              {lines.map((a) => (
                <li
                  key={a.id}
                  style={{
                    padding: "var(--space-2) 0",
                    borderBottom: "1px solid var(--color-border-soft)",
                    display: "flex",
                    gap: "var(--space-2)",
                    alignItems: "flex-start",
                  }}
                >
                  {a.hasImage ? (
                    <AdjustmentImageThumb id={a.id} />
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span>
                      <strong>
                        {a.kind === "BONUS" ? "+" : "−"} {formatEuro(a.amountEur)}
                      </strong>{" "}
                      <span className="muted">· {ADJUSTMENT_KIND_LABEL[a.kind]}</span>
                    </span>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {new Date(a.occurredAt).toLocaleString()} · by {a.recordedBy.fullName}
                    </div>
                    <div style={{ fontSize: 13, marginTop: 2 }}>{a.reason}</div>
                  </div>
                  {canDelete(a) ? (
                    <button
                      type="button"
                      className="button plain"
                      style={{ fontSize: 12 }}
                      disabled={deletingId === a.id}
                      onClick={() => void handleDelete(a.id)}
                    >
                      {deletingId === a.id ? "…" : "Delete"}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
